import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, X, ShoppingBag } from "lucide-react";

// HP3-only — "Four steps, made to work together." Collections shown as
// product-like cards; each card opens a right slide-over drawer to CHOOSE one
// product for that step (affordance: "Choose Step N →", not a direct add).
// A 1→4 progress bar fills as steps are chosen; completing all 4 reveals an
// "Add routine to cart" bar. Squared CTAs, near-full-width (matches slider).
const A = "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/";
const R = "https://www.riversol.com/cdn/shop";
const pr = (t, price, image) => ({ id: t, title: t, price, image });

const STEPS = [
  {
    name: "Preparation", blurb: "Gentle, purifying cleansers to hydrate & restore.",
    from: 18, image: A + "2pjyupsq_image.png",
    products: [
      pr("Hydrating Cream Cleanser", 23, R + "/products/White_BG_-_Cream_Cleanser.png?v=1776098620&width=800"),
      pr("Refreshing Gel Cleanser", 18, R + "/files/NewGelCleanserV2.png?v=1776098691&width=800"),
      pr("Micellar Water Makeup Remover", 12, R + "/files/MicellarV2_88746818-4a3e-41ff-af0c-7fcca79cd760.png?v=1776096171&width=800"),
    ],
  },
  {
    name: "Treatment", blurb: "Targeted serums & treatments to soften fine lines & texture.",
    from: 37, image: A + "5z1xcd6x_image.png",
    products: [
      pr("Exfoliating Glycolic Peel", 36, R + "/products/WhiteBG-Peel-XL.png?v=1776095551&width=800"),
      pr("Retinol Treatment", 53, R + "/files/NewRetinol-V2.png?v=1776101320&width=800"),
      pr("Redness Calming Mask", 38, R + "/files/Redness_Calming_Mask.png?v=1776115223&width=800"),
      pr("Retinol Neck Cream", 75, R + "/files/RetinolNeckCream_bb5f42ea-26a2-4c65-b6f4-2ebcc027df28.png?v=1776125270&width=800"),
    ],
  },
  {
    name: "Hydration", blurb: "Moisturizers for brighter, smoother, deeply hydrated skin.",
    from: 36, image: A + "5vir9p0b_image.png",
    products: [
      pr("Hydrating Body Lotion", 24, R + "/files/HydratingBodyLotion.png?v=1776125888&width=800"),
      pr("Daily Moisturizing Cream", 41, R + "/products/dmc_8215c1a3-320f-40e2-8cda-af33d45a4928.png?v=1776098827&width=800"),
      pr("Eye Repair Treatment", 46, R + "/products/eye-repair.png?v=1776097214&width=800"),
      pr("Moisturizing Concentrate", 38, R + "/products/concentrate_304fc294-9cce-4bf5-990a-d8e5cbb07fd7.png?v=1776095471&width=800"),
    ],
  },
  {
    name: "Protection", blurb: "100% mineral SPF with niacinamide to shield & protect daily.",
    from: 18, image: A + "nrcxef2p_image.png",
    products: [
      pr("Daily Glow Mineral Sunscreen SPF 50+", 39, R + "/files/Daily-Glow-Mineral-Sunscreen-over-texture-and-aplicator_UNTINTED.png?v=1776126826&width=800"),
      pr("SPF 40+ Tinted Sunscreen Lip Oil", 24, R + "/files/Broad-Spectrum-SPF-40-Lip-Oil_PPY.png?v=1776097706&width=800"),
    ],
  },
];

export const FourStepsV3 = () => {
  const [chosen, setChosen] = useState({}); // stepIndex -> product
  const [openStep, setOpenStep] = useState(null);
  const count = Object.keys(chosen).length;
  const complete = count === STEPS.length;
  const step = openStep != null ? STEPS[openStep] : null;

  const pickProduct = (p) => { setChosen((c) => ({ ...c, [openStep]: p })); setOpenStep(null); };

  return (
    <section className="bg-white py-20 lg:py-28" data-testid="home-four-steps-v3">
      <div className="max-w-[1800px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10 lg:mb-14">
          <h2 className="text-[32px] sm:text-[40px] lg:text-[52px] leading-[1.03] tracking-[-0.02em] text-neutral-900 max-w-[720px]">
            Four steps, made to <span className="italic text-riversol-blue">work together.</span>
          </h2>
          <a href="https://www.riversol.com/collections/all" data-testid="four-steps-shop-all"
            className="hidden lg:inline-flex items-center gap-2 border border-neutral-300 px-6 py-3 text-[12px] tracking-[0.14em] uppercase font-medium text-neutral-900 hover:border-riversol-blue hover:text-riversol-blue transition-colors">
            Shop All Products <ArrowRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => {
            const sel = chosen[i];
            return (
              <div key={s.name} data-testid={`step-card-${i + 1}`}
                onClick={() => setOpenStep(i)}
                className="group flex flex-col border border-neutral-200 bg-white overflow-hidden cursor-pointer hover:border-riversol-blue/40 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                <div className="relative aspect-square bg-riversol-sand overflow-hidden">
                  <img src={s.image} alt={s.name} draggable={false}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.18em] uppercase bg-white/90 text-neutral-900 px-2.5 py-1">Step {i + 1}</span>
                  {sel && <span className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-riversol-blue text-white"><Check size={15} strokeWidth={2.4} /></span>}
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="text-[17px] font-medium text-neutral-900">{s.name}</h3>
                  <p className="mt-1.5 text-[13px] leading-[1.45] text-neutral-600 flex-1">{s.blurb}</p>
                  <div className="mt-4 text-[11px] font-mono tracking-[0.16em] uppercase text-neutral-400">
                    {s.products.length} options
                  </div>
                  <div className="mt-1 text-[15px] text-neutral-900">
                    {sel ? <span className="text-riversol-blue font-medium">{sel.title} · ${sel.price}</span> : <>From <span className="font-semibold">${s.from}</span></>}
                  </div>
                  <button type="button" data-testid={`choose-step-${i + 1}`}
                    onClick={(e) => { e.stopPropagation(); setOpenStep(i); }}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-riversol-blue text-white px-6 py-3.5 text-[12px] tracking-[0.14em] uppercase font-medium hover:bg-riversol-blueLight transition-colors">
                    {sel ? "Change selection" : `Choose Step ${i + 1}`} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress 1 -> 4 + always-present Add-routine CTA (right) */}
        <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12" data-testid="four-steps-progress">
          <div className="flex-1 w-full">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-800 font-medium">
                Your routine · {count} of {STEPS.length} steps
              </span>
              {complete && <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-riversol-blue">Routine complete</span>}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {STEPS.map((_, i) => (
                <div key={i} className="h-1.5 bg-neutral-200 overflow-hidden">
                  <div className={`h-full bg-riversol-blue transition-all duration-500 ${i < count ? "w-full" : "w-0"}`} />
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            disabled={!complete}
            data-testid="add-routine-to-cart"
            className={`shrink-0 w-full lg:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 text-[12px] tracking-[0.14em] uppercase font-medium transition-colors ${complete ? "bg-riversol-blue text-white hover:bg-riversol-blueLight cursor-pointer" : "bg-riversol-blue/[0.08] text-riversol-blue/40 cursor-not-allowed"}`}
          >
            <ShoppingBag size={15} /> Add routine to cart
          </button>
        </div>
      </div>

      {/* Right slide-over product drawer */}
      <AnimatePresence>
        {step && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpenStep(null)} className="fixed inset-0 bg-black/40 z-[70]" data-testid="step-drawer-overlay" />
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-0 right-0 h-full w-full max-w-[440px] bg-white z-[71] flex flex-col shadow-2xl" data-testid="step-drawer">
              <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-200">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">Step {openStep + 1}</div>
                  <div className="text-[18px] font-medium text-neutral-900">Choose your {step.name}</div>
                </div>
                <button type="button" onClick={() => setOpenStep(null)} data-testid="step-drawer-close"
                  className="w-9 h-9 flex items-center justify-center border border-neutral-200 hover:border-neutral-400 transition-colors"><X size={16} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {step.products.map((p) => {
                  const active = chosen[openStep]?.id === p.id;
                  return (
                    <div key={p.id} data-testid={`drawer-product-${p.id}`}
                      className={`flex items-center gap-4 border p-3 ${active ? "border-riversol-blue" : "border-neutral-200"}`}>
                      <div className="w-16 h-16 bg-riversol-sand shrink-0"><img src={p.image} alt={p.title} className="w-full h-full object-contain" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-medium text-neutral-900 truncate">{p.title}</div>
                        <div className="text-[13px] text-neutral-600">${p.price}</div>
                      </div>
                      <button type="button" onClick={() => pickProduct(p)} data-testid={`drawer-select-${p.id}`}
                        className={`shrink-0 px-4 py-2.5 text-[11px] tracking-[0.12em] uppercase font-medium transition-colors ${active ? "bg-riversol-blue text-white" : "border border-riversol-blue text-riversol-blue hover:bg-riversol-blue hover:!text-white"}`}>
                        {active ? "Selected" : "Select"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FourStepsV3;
